/**
 * DOM-based PDF generation for the benefits page.
 * Captures the rendered content and converts it to a multi-page PDF with smart
 * page breaks at section boundaries.
 */

// eslint-disable-next-line import/no-extraneous-dependencies -- html2canvas is in client/package.json
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  CAPTURE_PADDING_PX,
  CAPTURE_SCALE,
  CAPTURE_STABILIZE_MS,
  CAPTURE_WIDTH_PX,
  PAGE_BREAK_SEARCH_RADIUS_PX,
  PAGE_BREAK_STRIP_HEIGHT_PX,
  PAGE_BREAK_WHITE_THRESHOLD,
  PAGE_TOP_BUFFER_PX,
  PDF_PAGE_HEIGHT_PT,
  PDF_PAGE_MARGIN_PT,
  PDF_PAGE_WIDTH_PT,
  PDF_SCALE,
  SECTION_IDS,
} from './constants';

const camelToKebab = (s: string): string =>
  s.replace(/([A-Z])/g, '-$1').toLowerCase();

const toStyle = (o: Record<string, string>): string =>
  Object.entries(o)
    .map(([k, v]) => `${camelToKebab(k)}:${v}`)
    .join(';');

const getStripBrightness = (
  ctx: CanvasRenderingContext2D,
  fullWidthPx: number,
  y: number,
): number => {
  const data = ctx.getImageData(0, y, fullWidthPx, PAGE_BREAK_STRIP_HEIGHT_PX);
  const d = data.data;
  let sum = 0;
  for (let i = 0; i < d.length; i += 4) {
    sum += d[i + 3] < 128 ? 255 : (d[i] + d[i + 1] + d[i + 2]) / 3;
  }
  return sum / (d.length / 4);
};

/** Scan a y-range and return the y with the brightest (whitest) horizontal strip. */
const findBrightestY = (
  ctx: CanvasRenderingContext2D,
  fullWidthPx: number,
  fullHeightPx: number,
  fromY: number,
  toY: number,
): { y: number; score: number } => {
  const clampedFrom = Math.max(0, fromY);
  const clampedTo = Math.min(fullHeightPx - PAGE_BREAK_STRIP_HEIGHT_PX, toY);
  let bestY = clampedFrom;
  let bestScore = 0;
  for (let y = clampedFrom; y <= clampedTo; y += 2) {
    const brightness = getStripBrightness(ctx, fullWidthPx, y);
    if (brightness > bestScore) {
      bestScore = brightness;
      bestY = y;
    }
  }
  return { y: bestY, score: bestScore };
};

/** Small radius for brightness refinement around a section boundary. */
const BOUNDARY_REFINE_RADIUS_PX = 48;

const findSafeBreakY = (
  ctx: CanvasRenderingContext2D,
  fullWidthPx: number,
  fullHeightPx: number,
  centerY: number,
  radius: number,
  prevBreakY: number,
  sectionBoundariesOffScreen: number[],
): number => {
  const minBreakY = prevBreakY + 1;
  const yStart = Math.max(minBreakY, centerY - radius);
  const yEndStrip = Math.min(
    fullHeightPx - PAGE_BREAK_STRIP_HEIGHT_PX,
    centerY + radius,
  );

  const valid = sectionBoundariesOffScreen.filter((by) => by > prevBreakY);
  const bestBelow = valid
    .filter((by) => by <= centerY)
    .reduce((a, b) => Math.max(a, b), -1);
  const bestSectionY =
    bestBelow >= 0 ? bestBelow : (valid.find((by) => by >= centerY) ?? null);
  if (bestSectionY != null) {
    // Refine around the section boundary using brightness to avoid cutting
    // through table rows. Scan a small window and pick the whitest strip.
    const candidate = bestSectionY - PAGE_TOP_BUFFER_PX;
    const { y: refinedY, score } = findBrightestY(
      ctx,
      fullWidthPx,
      fullHeightPx,
      Math.max(minBreakY, candidate - BOUNDARY_REFINE_RADIUS_PX),
      candidate + BOUNDARY_REFINE_RADIUS_PX,
    );
    return Math.max(
      minBreakY,
      score >= PAGE_BREAK_WHITE_THRESHOLD ? refinedY : candidate,
    );
  }

  const { y: bestY, score: bestScore } = findBrightestY(
    ctx,
    fullWidthPx,
    fullHeightPx,
    yStart,
    yEndStrip,
  );
  return Math.max(
    minBreakY,
    bestScore >= PAGE_BREAK_WHITE_THRESHOLD ? bestY : centerY,
  );
};

const getSectionBoundariesFromClone = (clone: HTMLDivElement): number[] => {
  const boundaries = new Set<number>([0]);
  const cloneRect = clone.getBoundingClientRect();
  const cloneTop = cloneRect.top;

  const addBoundary = (el: Element, bottomOnly = false) => {
    if (!(el instanceof HTMLElement)) return;
    const rect = el.getBoundingClientRect();
    const topRel = rect.top - cloneTop;
    const bottomRel = rect.bottom - cloneTop;
    if (!bottomOnly && topRel >= 0) boundaries.add(topRel);
    if (bottomRel <= clone.offsetHeight) boundaries.add(bottomRel);
  };

  for (const id of SECTION_IDS) {
    const el = clone.querySelector(`[id="${id}"]`);
    if (!el) continue;
    addBoundary(el);
  }

  // Also discover Coverage Details sub-category sections (dynamic IDs ending in "-details").
  // Only add their BOTTOM as a boundary so breaks happen between categories,
  // not at the top of a sub-category (which would orphan the card header).
  const coverageDetails = clone.querySelector('[id="coverage-details"]');
  if (coverageDetails) {
    coverageDetails
      .querySelectorAll('[id$="-details"]')
      .forEach((el) => addBoundary(el, true));
  }

  boundaries.add(clone.offsetHeight);
  return [...boundaries].sort((a, b) => a - b);
};

const createCaptureOverlay = (
  contentArea: HTMLElement,
  element: HTMLDivElement,
): { overlay: HTMLDivElement; clone: HTMLDivElement } => {
  const rect = contentArea.getBoundingClientRect();
  const overlayWidthPx = CAPTURE_WIDTH_PX + 2 * CAPTURE_PADDING_PX;
  const overlay = document.createElement('div');
  overlay.style.cssText = toStyle({
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${overlayWidthPx}px`,
    minHeight: `${rect.height}px`,
    overflow: 'visible',
    opacity: '0',
    pointerEvents: 'none',
    zIndex: '2147483647',
  });

  const clone = element.cloneNode(true) as HTMLDivElement;
  clone.style.cssText = toStyle({
    width: `${CAPTURE_WIDTH_PX}px`,
    minWidth: `${CAPTURE_WIDTH_PX}px`,
    height: 'auto',
    overflow: 'visible',
    maxWidth: 'none',
    boxSizing: 'border-box',
    padding: `${CAPTURE_PADDING_PX}px`,
  });

  overlay.appendChild(clone);
  document.body.appendChild(overlay);
  return { overlay, clone };
};

const prepareCloneForPdf = (clone: HTMLDivElement): void => {
  const rules: { selector: string; display: string }[] = [
    { selector: '[data-pdf-hide="true"]', display: 'none' },
    { selector: '[data-pdf-show-in-capture]', display: 'block' },
  ];
  rules.forEach(({ selector, display }) => {
    clone.querySelectorAll(selector).forEach((el) => {
      if (el instanceof HTMLElement) el.style.display = display;
    });
  });

  // Hide all icons in the PDF.
  clone.querySelectorAll('svg').forEach((svg) => {
    const wrapper = svg.parentElement;
    if (wrapper && wrapper !== clone && wrapper instanceof HTMLElement) {
      wrapper.style.display = 'none';
    }
  });

  // Remove top padding inside section card headers (above title/tabs, inside the border).
  clone.querySelectorAll('div.vai-p-4').forEach((el) => {
    if (el instanceof HTMLElement && el.previousElementSibling === null) {
      el.style.paddingTop = '0';
    }
  });

  // Replace Plan Summary toggle (Dental Care | Orthodontics) with the selected label only.
  const planSummary = clone.querySelector('#plan-summary-section');
  const toggle = planSummary?.querySelector('[role="group"][aria-label]');
  if (toggle?.parentElement && toggle instanceof HTMLElement) {
    const pressed = toggle.querySelector('[aria-pressed="true"]');
    const labelText = pressed?.textContent?.trim() ?? 'Dental Care';
    const span = document.createElement('span');
    span.className = 'vai-text-sm vai-font-medium vai-text-gray-700';
    span.textContent = labelText;
    toggle.parentElement.replaceChild(span, toggle);
  }

  // Fix "Deductible Met" chip in PDF: align chip to row bottom and vertically center text inside.
  if (planSummary) {
    const chips = planSummary.querySelectorAll(
      'div[class*="vai-rounded-full"][class*="vai-py-2"]',
    );
    for (const el of chips) {
      if (
        el.textContent?.includes('Deductible Met') &&
        el instanceof HTMLElement
      ) {
        el.style.paddingTop = '0';
        el.style.alignSelf = 'flex-end';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        const textSpan = el.querySelector('span');
        if (textSpan instanceof HTMLElement) {
          textSpan.style.lineHeight = '1';
        }
        break;
      }
    }
  }
};

/**
 * Max canvas dimension limits. Browsers silently produce a dead backing store
 * for canvases that exceed their GPU texture limit. Chrome desktop supports
 * 65 535; Safari / Firefox may be lower (16 384 – 32 768). We try the high
 * limit first, detect a dead canvas, then retry with the conservative limit.
 */
const MAX_CANVAS_DIM_HIGH = 65535;
const MAX_CANVAS_DIM_LOW = 16384;

/** Returns true if the canvas backing store is dead (all pixels are zeroed). */
const isCanvasDead = (canvas: HTMLCanvasElement): boolean => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  // html2canvas fills the background with white (255,255,255,255).
  // A dead backing store reads back as (0,0,0,0).
  const pixel = ctx.getImageData(0, 0, 1, 1).data;
  return pixel[0] === 0 && pixel[1] === 0 && pixel[2] === 0 && pixel[3] === 0;
};

/** Capture the clone DOM element to an HTMLCanvasElement via html2canvas. */
const captureCloneToCanvas = async (
  clone: HTMLDivElement,
): Promise<HTMLCanvasElement> => {
  await new Promise((r) =>
    requestAnimationFrame(() => requestAnimationFrame(r)),
  );
  await new Promise((r) => setTimeout(r, CAPTURE_STABILIZE_MS));

  const html2canvasOpts = {
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    ignoreElements: (el: Element) =>
      el.getAttribute?.('data-pdf-hide') === 'true',
  };

  const computeScale = (maxDim: number) =>
    Math.min(
      CAPTURE_SCALE,
      maxDim / Math.max(clone.offsetWidth, 1),
      maxDim / Math.max(clone.offsetHeight, 1),
    );

  // First attempt: generous limit (Chrome desktop)
  let canvas = await html2canvas(clone, {
    ...html2canvasOpts,
    scale: computeScale(MAX_CANVAS_DIM_HIGH),
  });

  // Detect dead backing store and retry with conservative limit
  if (canvas.width > 0 && canvas.height > 0 && isCanvasDead(canvas)) {
    canvas = await html2canvas(clone, {
      ...html2canvasOpts,
      scale: computeScale(MAX_CANVAS_DIM_LOW),
    });
  }

  if (canvas.width === 0 || canvas.height === 0 || isCanvasDead(canvas)) {
    throw new Error(
      `html2canvas produced an unusable canvas (${canvas.width}x${canvas.height}). ` +
        `Clone dimensions: ${clone.offsetWidth}x${clone.offsetHeight}, isConnected=${clone.isConnected}`,
    );
  }

  return canvas;
};

/**
 * Minimum distance between consecutive page breaks (fraction of page height).
 *  Prevents nearly-empty pages without creating oversized slices.
 */
const MIN_SLICE_HEIGHT_RATIO = 0.3;

const computeBreakYs = (
  ctx: CanvasRenderingContext2D,
  fullWidthPx: number,
  fullHeightPx: number,
  pageHeightPx: number,
  numPages: number,
  clone: HTMLDivElement,
): number[] => {
  const cloneHeight = clone.offsetHeight;
  const sectionBoundariesClone = getSectionBoundariesFromClone(clone);
  const sectionBoundariesOffScreen = sectionBoundariesClone.map(
    (cy) => (cy * fullHeightPx) / cloneHeight,
  );

  const minSliceHeight = pageHeightPx * MIN_SLICE_HEIGHT_RATIO;
  const breakYs: number[] = [0];
  for (let p = 1; p < numPages; p++) {
    const idealY = p * pageHeightPx;
    const prevBreakY = breakYs[breakYs.length - 1];

    // Skip this page break if it would be too close to the previous one
    if (idealY - prevBreakY < minSliceHeight) continue;

    const safeY = findSafeBreakY(
      ctx,
      fullWidthPx,
      fullHeightPx,
      idealY,
      PAGE_BREAK_SEARCH_RADIUS_PX,
      prevBreakY,
      sectionBoundariesOffScreen,
    );

    // Double-check the safe break isn't too close to the previous one
    if (safeY - prevBreakY < minSliceHeight) continue;

    breakYs.push(safeY);
  }
  breakYs.push(fullHeightPx);

  return breakYs;
};

/** Content area in PDF points (full page minus margins on each side). */
const CONTENT_WIDTH_PT = PDF_PAGE_WIDTH_PT - 2 * PDF_PAGE_MARGIN_PT;
const CONTENT_HEIGHT_PT = PDF_PAGE_HEIGHT_PT - 2 * PDF_PAGE_MARGIN_PT;

/**
 * Render a single PDF page from a vertical sub-slice of the source canvas.
 * Content is drawn into the margin-inset content area; the surrounding margin
 * is left white so text is never flush against (or cut off by) the page edge.
 */
const renderPageSlice = (
  pdf: jsPDF,
  sourceCanvas: HTMLCanvasElement,
  srcY: number,
  srcH: number,
  sourceWidth: number,
  widthScale: number,
  contentWidthPx: number,
  contentHeightPx: number,
): void => {
  const sliceCanvas = document.createElement('canvas');
  sliceCanvas.width = contentWidthPx;
  sliceCanvas.height = contentHeightPx;
  const sliceCtx = sliceCanvas.getContext('2d');
  if (!sliceCtx) throw new Error('Could not get slice canvas context');
  sliceCtx.imageSmoothingEnabled = true;
  sliceCtx.imageSmoothingQuality = 'high';
  sliceCtx.fillStyle = '#ffffff';
  sliceCtx.fillRect(0, 0, contentWidthPx, contentHeightPx);
  const drawW = sourceWidth * widthScale;
  const drawH = srcH * widthScale;
  sliceCtx.drawImage(
    sourceCanvas,
    0,
    srcY,
    sourceWidth,
    srcH,
    0,
    0,
    drawW,
    drawH,
  );
  const sliceDataUrl = sliceCanvas.toDataURL('image/jpeg', 0.92);
  pdf.addImage(
    sliceDataUrl,
    'JPEG',
    PDF_PAGE_MARGIN_PT,
    PDF_PAGE_MARGIN_PT,
    CONTENT_WIDTH_PT,
    CONTENT_HEIGHT_PT,
  );
};

const addSlicesToPdf = (
  pdf: jsPDF,
  sourceCanvas: HTMLCanvasElement,
  breakYs: number[],
  sourceWidth: number,
  contentWidthPx: number,
  contentHeightPx: number,
): void => {
  // Scale source to fill the content area width (margins handled by renderPageSlice).
  const widthScale = contentWidthPx / sourceWidth;
  // Max source-pixel height that fits one page's content area at this scale.
  const maxSrcHPerPage = contentHeightPx / widthScale;
  let isFirstPage = true;

  for (let j = 0; j < breakYs.length - 1; j++) {
    const sliceStart = breakYs[j];
    const sliceEnd = breakYs[j + 1];
    const sliceH = sliceEnd - sliceStart;

    // Tile this slice across as many pages as needed at full size.
    const subPages = Math.max(1, Math.ceil(sliceH / maxSrcHPerPage));
    const subSliceH = sliceH / subPages;

    for (let s = 0; s < subPages; s++) {
      if (!isFirstPage) pdf.addPage();
      isFirstPage = false;

      const subSrcY = sliceStart + s * subSliceH;
      const subSrcH = Math.min(subSliceH, sliceEnd - subSrcY);
      renderPageSlice(
        pdf,
        sourceCanvas,
        subSrcY,
        subSrcH,
        sourceWidth,
        widthScale,
        contentWidthPx,
        contentHeightPx,
      );
    }
  }
};

const buildPdfBlob = async (
  capturedCanvas: HTMLCanvasElement,
  clone: HTMLDivElement,
): Promise<Blob> => {
  const capW = capturedCanvas.width;
  const capH = capturedCanvas.height;

  // Page height expressed in captured-canvas pixel coordinates.
  // Use the *content area* (page minus margins) so we don't pack more content
  // per page than actually fits inside the margins.
  const pageHeightCap = capW * (CONTENT_HEIGHT_PT / CONTENT_WIDTH_PT);
  const numPages = Math.ceil(capH / pageHeightCap);

  // Output content-area size in pixels (at PDF_SCALE) for slice canvas quality
  const contentWidthPx = Math.round(CONTENT_WIDTH_PT * PDF_SCALE);
  const contentHeightPx = Math.round(CONTENT_HEIGHT_PT * PDF_SCALE);

  // Use the captured canvas directly for break-point brightness scanning
  const ctx = capturedCanvas.getContext('2d');
  if (!ctx) throw new Error('Could not get captured canvas context');

  const breakYs = computeBreakYs(
    ctx,
    capW,
    capH,
    pageHeightCap,
    numPages,
    clone,
  );

  const pdf = new jsPDF('p', 'pt', 'a4');
  addSlicesToPdf(
    pdf,
    capturedCanvas,
    breakYs,
    capW,
    contentWidthPx,
    contentHeightPx,
  );
  return pdf.output('blob');
};

/**
 * Generates a PDF blob from the benefits page content element.
 * Creates an off-screen overlay, captures with html2canvas, then builds a
 * multi-page PDF with page breaks at section boundaries. Caller must ensure
 * the element is mounted and has a parent.
 */
export const generateBenefitsPdfBlob = async (
  element: HTMLDivElement,
): Promise<Blob> => {
  const contentArea = element.parentElement;
  if (!contentArea) {
    throw new Error('Benefits content has no parent');
  }

  const { overlay, clone } = createCaptureOverlay(contentArea, element);
  try {
    prepareCloneForPdf(clone);
    const canvas = await captureCloneToCanvas(clone);
    return await buildPdfBlob(canvas, clone);
  } finally {
    overlay.remove();
  }
};
